import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useHistory, useLocation } from "react-router-dom";
import { ScrollNavbar, SearchSection } from "../../components";
import searchService, {
  City,
  Price,
  SearchRequest,
} from "../../services/searchService";
import CarDetailsCard from "../../components/CarDetailsCard/CarDetailsCard";
import Swal from "sweetalert2";
import { Sidebar } from "../../components";
import { FilterOption } from "../../components/Sidebar/Sidebar";
import {
  VerticalType,
  TripType,
  VERTICALS,
  TRIP_TYPES,
  getHourlyDurationKey,
  isSourceFieldVisible,
  isDestinationFieldVisible,
  isReturnLegRequired,
} from "../../constants/tripTypes";
import {
  DrivingDistanceResult,
  getDrivingDistance,
} from "../../utils/distanceHelper";
import { PlaceSelection } from "../../components/PlaceAutocompleteInput/PlaceAutocompleteInput";
import { findNearestCity } from "../../utils/geo";

const Dashboard = () => {
  const { user } = useAuth();
  const history = useHistory();
  const location = useLocation();

  // Form state
  const [activeTab, setActiveTab] = useState<VerticalType>(VERTICALS.INTERCITY);
  const [tripType, setTripType] = useState<TripType>(TRIP_TYPES.ONEWAY);
  const [source, setSource] = useState<City | null>(null);
  const [destination, setDestination] = useState<City | null>(null);
  const [departureDate, setDepartureDate] = useState<string>("");
  const [returnDate, setReturnDate] = useState<string>("");
  const [pickupTime, setPickupTime] = useState<string>("");
  const [dropTime, setDropTime] = useState<string>("");
  const [sourceSearch, setSourceSearch] = useState<string>("");
  const [destSearch, setDestSearch] = useState<string>("");
  const [distanceKm, setDistanceKm] = useState<number | undefined>(undefined);

  // Exact pickup/drop address captured via Google Places Autocomplete - used
  // for the precise distance calc and the booking's pickup/drop address text.
  // `source`/`destination` (City) is still derived from these (nearest match)
  // since Price/Plan routes are configured per city pair, not exact address.
  const [sourceAddress, setSourceAddress] = useState<PlaceSelection | null>(
    null
  );
  const [destinationAddress, setDestinationAddress] =
    useState<PlaceSelection | null>(null);
  const [allCities, setAllCities] = useState<City[]>([]);

  useEffect(() => {
    searchService
      .getAllCities()
      .then((response) => {
        if (response.success && response.data) setAllCities(response.data);
      })
      .catch((error) => console.error("Failed to load cities list:", error));
  }, []);

  const handleSourcePlaceSelected = (place: PlaceSelection) => {
    setSourceSearch(place.address);
    setSourceAddress(place);
    setSource(findNearestCity(allCities, place.lat, place.lng));
  };

  const handleDestinationPlaceSelected = (place: PlaceSelection) => {
    setDestSearch(place.address);
    setDestinationAddress(place);
    setDestination(findNearestCity(allCities, place.lat, place.lng));
  };

  const handleSwapLocations = () => {
    const tempSource = source;
    const tempSourceSearch = sourceSearch;
    const tempSourceAddress = sourceAddress;

    setSource(destination);
    setSourceSearch(destSearch);
    setSourceAddress(destinationAddress);

    setDestination(tempSource);
    setDestSearch(tempSourceSearch);
    setDestinationAddress(tempSourceAddress);
  };

  const handleDistanceComputed = (result: DrivingDistanceResult | null) => {
    setDistanceKm(result ? result.distanceMeters / 1000 : undefined);
  };

  // Search state
  const [searchResults, setSearchResults] = useState<Price[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchAttempted, setSearchAttempted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [initialSearchDone, setInitialSearchDone] = useState<boolean>(false);

  // Sidebar filters - applied purely client-side against searchResults,
  // never sent to the search API.
  const [filters, setFilters] = useState<{
    cabType: string[];
    fuelType: string[];
    cabModel: string[];
  }>({ cabType: [], fuelType: [], cabModel: [] });

  const handleFilterChange = <T extends string>(
    filterType: T,
    value: string,
    isChecked: boolean
  ) => {
    setFilters((prev) => {
      const key = filterType as keyof typeof prev;
      const current = prev[key];
      return {
        ...prev,
        [key]: isChecked
          ? [...current, value]
          : current.filter((v) => v !== value),
      };
    });
  };

  // Cab Model options are derived from whatever the search actually
  // returned - vehicle_model is open-ended on the backend, so a hardcoded
  // option list would silently go stale/mismatch. A "any" vehicle_model (a
  // real enum value - see fusion_backend's Price model) means the price
  // isn't tied to one specific model, so it's not a meaningful checkbox
  // choice and is left out of the list (it's still always shown - see the
  // wildcard check in filteredResults below).
  const cabModelOptions: FilterOption[] = useMemo(() => {
    const seen = new Map<string, FilterOption>();
    for (const car of searchResults) {
      if (car.vehicle_model?.toLowerCase() === "any") continue;
      const id = `${car.vehicle_manufacturer}::${car.vehicle_model}`;
      if (!seen.has(id)) {
        seen.set(id, {
          id,
          label: `${car.vehicle_manufacturer} ${car.vehicle_model}`,
        });
      }
    }
    return Array.from(seen.values());
  }, [searchResults]);

  const filteredResults: Price[] = useMemo(() => {
    return searchResults.filter((car) => {
      // Plan.vehicle_category has no "any" wildcard on the backend (it's a
      // required enum array), so this is always a literal comparison.
      const cabTypeMatch =
        filters.cabType.length === 0 ||
        (car.Plan.vehicle_category ?? []).some((c) =>
          filters.cabType.includes(c)
        );

      // fuel_type "any" (a real enum value) means the price works with any
      // fuel, so it should pass every fuel filter rather than none of them.
      const fuelTypeMatch =
        filters.fuelType.length === 0 ||
        car.fuel_type?.toLowerCase() === "any" ||
        filters.fuelType.some(
          (f) => f.toLowerCase() === car.fuel_type?.toLowerCase()
        );

      // Same "any" wildcard for vehicle_model.
      const cabModelId = `${car.vehicle_manufacturer}::${car.vehicle_model}`;
      const cabModelMatch =
        filters.cabModel.length === 0 ||
        car.vehicle_model?.toLowerCase() === "any" ||
        filters.cabModel.includes(cabModelId);

      return cabTypeMatch && fuelTypeMatch && cabModelMatch;
    });
  }, [searchResults, filters]);

  // Determine if search is enabled
  const isSearchEnabled = (() => {
    if (!departureDate || !pickupTime) return false;
    if (isSourceFieldVisible(activeTab, tripType) && !source) return false;
    if (isDestinationFieldVisible(activeTab, tripType) && !destination) return false;
    if (isReturnLegRequired(tripType) && (!returnDate || !dropTime)) return false;
    return true;
  })();

  // Load data from URL parameters - only once on initial load
  useEffect(() => {
    const loadFromUrl = async () => {
      setIsLoading(true);

      try {
        // Parse URL parameters
        const queryParams = new URLSearchParams(location.search);

        // Process vertical + trip type (same param scheme Landing.tsx emits)
        const verticalParam = queryParams.get("vertical");
        const parsedVertical: VerticalType = (
          Object.values(VERTICALS) as string[]
        ).includes(verticalParam || "")
          ? (verticalParam as VerticalType)
          : VERTICALS.INTERCITY;

        const tripTypeParam = queryParams.get("trip-type");
        const parsedTripType: TripType = (
          Object.values(TRIP_TYPES) as string[]
        ).includes(tripTypeParam || "")
          ? (tripTypeParam as TripType)
          : TRIP_TYPES.ONEWAY;

        setActiveTab(parsedVertical);
        setTripType(parsedTripType);

        // Process simple parameters
        const departureDateParam = queryParams.get("departure-date") || "";
        const returnDateParam = queryParams.get("return-date") || "";
        const pickupTimeParam = queryParams.get("pickup-time") || "";
        const dropTimeParam = queryParams.get("drop-time") || "";

        setDepartureDate(departureDateParam);
        setReturnDate(returnDateParam);
        setPickupTime(pickupTimeParam);
        setDropTime(dropTimeParam);

        // Process city parameters
        const sourceParam = queryParams.get("source");
        const destParam = queryParams.get("destination");

        // Initialize city data
        let sourceCityData: City | null = null;
        let destCityData: City | null = null;

        // Fetch cities if parameters exist
        if (sourceParam) {
          try {
            const sourceCities = await searchService.searchCities(
              sourceParam,
              5
            );
            if (sourceCities && sourceCities.length > 0) {
              sourceCityData = sourceCities[0];
              setSource(sourceCityData);
              setSourceSearch(sourceCityData.name);
            }
          } catch (error) {
            console.error("Error fetching source city:", error);
          }
        }

        if (destParam) {
          try {
            const destCities = await searchService.searchCities(destParam, 5);
            if (destCities && destCities.length > 0) {
              destCityData = destCities[0];
              setDestination(destCityData);
              setDestSearch(destCityData.name);
            }
          } catch (error) {
            console.error("Error fetching destination city:", error);
          }
        }

        // Perform initial search if we have all required parameters
        const canSearch =
          (sourceParam || !isSourceFieldVisible(parsedVertical, parsedTripType)) &&
          (destParam || !isDestinationFieldVisible(parsedVertical, parsedTripType)) &&
          departureDateParam &&
          pickupTimeParam &&
          (!isReturnLegRequired(parsedTripType) ||
            (returnDateParam && dropTimeParam));

        if (canSearch) {
          // Resolve the implicit airport-side city for Airport searches
          let apiSourceCity = sourceCityData;
          let apiDestCity = destCityData;

          if (parsedVertical === VERTICALS.AIRPORT) {
            if (parsedTripType === TRIP_TYPES.PICKUP && destCityData) {
              const airportCity = await searchService.findAirportCity(
                destCityData.name
              );
              if (airportCity) {
                apiSourceCity = airportCity;
                setSource(airportCity);
              }
            } else if (parsedTripType === TRIP_TYPES.DROPOFF && sourceCityData) {
              const airportCity = await searchService.findAirportCity(
                sourceCityData.name
              );
              if (airportCity) {
                apiDestCity = airportCity;
                setDestination(airportCity);
              }
            }
          }

          const destinationId =
            parsedVertical === VERTICALS.HOURLY
              ? apiSourceCity?.id
              : apiDestCity?.id;

          if (apiSourceCity && destinationId) {
            const searchData: SearchRequest = {
              trip_type: parsedTripType,
              source: apiSourceCity.id,
              destination: destinationId,
            };

            if (parsedVertical === VERTICALS.HOURLY) {
              searchData.hours = getHourlyDurationKey(parsedTripType) ?? "4";
            } else if (apiDestCity) {
              // Live Google Maps route distance, computed here (not left to
              // the preview map's own effect) so it's ready before results
              // render - matters most for Airport, where one side is only
              // resolved above and was never visible for the map to key off.
              const distanceResult = await getDrivingDistance(
                { lat: apiSourceCity.latitude, lng: apiSourceCity.longitude },
                { lat: apiDestCity.latitude, lng: apiDestCity.longitude }
              );
              setDistanceKm(
                distanceResult ? distanceResult.distanceMeters / 1000 : undefined
              );
            }

            performSearch(searchData);
            setInitialSearchDone(true);
          }
        }
      } catch (error) {
        console.error("Error loading from URL:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadFromUrl();
    // This effect should only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Perform the actual search
  const performSearch = (searchData: SearchRequest) => {
    setIsSearching(true);
    setSearchResults([]);
    // Cab Model options are result-specific - drop any stale selection from
    // a previous search's result set.
    setFilters((prev) => ({ ...prev, cabModel: [] }));

    // Perform the search
    searchService
      .searchPrices(searchData)
      .then((response) => {
        if (response.success && response.data && response.data.length > 0) {
          setSearchResults(response.data);
        } else {
          setSearchResults([]);
        }
      })
      .catch((error) => {
        console.error("Search failed:", error);
        Swal.fire({
          icon: "error",
          title: "Search Error",
          text: "An unexpected error occurred while searching for cabs. Please try again.",
        });
      })
      .finally(() => {
        setIsSearching(false);
      });
  };

  // Handle search
  const handleSearch = async () => {
    setSearchAttempted(true);

    const sourceRequired = isSourceFieldVisible(activeTab, tripType);
    const destinationRequired = isDestinationFieldVisible(activeTab, tripType);
    const returnLegRequired = isReturnLegRequired(tripType);

    if ((!source && sourceRequired) || (!destination && destinationRequired)) {
      Swal.fire({
        icon: "error",
        title: "Missing Information",
        text:
          "Please select " +
          [sourceRequired && "source", destinationRequired && "destination"]
            .filter(Boolean)
            .join(" and ") +
          ".",
      });
      return;
    }

    if (!departureDate) {
      Swal.fire({
        icon: "error",
        title: "Missing Information",
        text: "Please select a departure date.",
      });
      return;
    }

    if (returnLegRequired && !returnDate) {
      Swal.fire({
        icon: "error",
        title: "Missing Information",
        text: "Please select a return date for your round trip.",
      });
      return;
    }

    if (!pickupTime) {
      Swal.fire({
        icon: "error",
        title: "Missing Information",
        text: "Please select a pickup time.",
      });
      return;
    }

    if (returnLegRequired && !dropTime) {
      Swal.fire({
        icon: "error",
        title: "Missing Information",
        text: "Please select a drop time for your round trip.",
      });
      return;
    }

    // Resolve the implicit airport-side city (source for Pickup, destination
    // for Drop-off) before building the search request.
    let searchSource = source;
    let searchDestination = destination;

    if (activeTab === VERTICALS.AIRPORT) {
      if (tripType === TRIP_TYPES.PICKUP) {
        const airportCity = await searchService.findAirportCity(
          destination!.name
        );
        if (!airportCity) {
          Swal.fire({
            icon: "error",
            title: "Not Available",
            text: `Airport transfer is not available for ${destination!.name} yet.`,
          });
          return;
        }
        searchSource = airportCity;
        setSource(airportCity);
      } else if (tripType === TRIP_TYPES.DROPOFF) {
        const airportCity = await searchService.findAirportCity(source!.name);
        if (!airportCity) {
          Swal.fire({
            icon: "error",
            title: "Not Available",
            text: `Airport transfer is not available for ${source!.name} yet.`,
          });
          return;
        }
        searchDestination = airportCity;
        setDestination(airportCity);
      }
    }

    const searchData: SearchRequest = {
      trip_type: tripType,
      source: searchSource!.id,
      destination:
        activeTab !== VERTICALS.HOURLY ? searchDestination!.id : searchSource!.id,
    };

    if (activeTab === VERTICALS.HOURLY) {
      searchData.hours = getHourlyDurationKey(tripType) ?? "4";
    } else {
      // Live Google Maps route distance for the resolved source/destination
      // pair - computed here, synchronously as part of the search, instead
      // of relying on the preview map's own effect. That map is gated on
      // field visibility, which for Airport hides exactly the side (the
      // airport) this function just resolved above, so it never ran for
      // Airport Drop-off and raced Airport Pickup's results on first search.
      const originLat = sourceAddress?.lat ?? searchSource?.latitude;
      const originLng = sourceAddress?.lng ?? searchSource?.longitude;
      const destLat = destinationAddress?.lat ?? searchDestination?.latitude;
      const destLng = destinationAddress?.lng ?? searchDestination?.longitude;

      if (originLat != null && originLng != null && destLat != null && destLng != null) {
        const distanceResult = await getDrivingDistance(
          { lat: originLat, lng: originLng },
          { lat: destLat, lng: destLng }
        );
        setDistanceKm(
          distanceResult ? distanceResult.distanceMeters / 1000 : undefined
        );
      } else {
        setDistanceKm(undefined);
      }
    }

    // Update URL with current search parameters (without triggering a reload)
    const params = new URLSearchParams();
    params.set("vertical", activeTab);
    params.set("trip-type", tripType);
    if (source) params.set("source", source.name);
    if (destination) params.set("destination", destination.name);
    if (departureDate) params.set("departure-date", departureDate);
    if (returnDate) params.set("return-date", returnDate);
    if (pickupTime) params.set("pickup-time", pickupTime);
    if (dropTime) params.set("drop-time", dropTime);
    if (activeTab === VERTICALS.HOURLY) {
      params.set("hourly-duration", getHourlyDurationKey(tripType) ?? "4");
    }

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });

    // Perform the search
    performSearch(searchData);
  };

  // Handle clear filter
  const handleClearFilter = () => {
    // Clear all form state
    setSource(null);
    setDestination(null);
    setSourceSearch("");
    setDestSearch("");
    setSourceAddress(null);
    setDestinationAddress(null);
    setDepartureDate("");
    setReturnDate("");
    setPickupTime("");
    setDropTime("");
    setDistanceKm(undefined);
    setSearchResults([]);
    setSearchAttempted(false);
    setFilters({ cabType: [], fuelType: [], cabModel: [] });

    // Clear URL parameters
    history.replace({
      pathname: location.pathname,
      search: "",
    });
  };

  // Determine if clear button should be shown
  const showClearButton = !!(
    source ||
    destination ||
    departureDate ||
    returnDate ||
    pickupTime ||
    dropTime ||
    sourceSearch ||
    destSearch ||
    searchResults.length > 0
  );

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <ScrollNavbar />
      <div className="w-full flex">
      <div className="w-1/4">
        <Sidebar
          onFilterChange={handleFilterChange}
          initialFilters={filters}
          cabModelOptions={cabModelOptions}
        />
      </div>
      <div className="max-w-[1400px] mx-auto py-3 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-visible">
          {isLoading ? (
            <div className="p-6 flex justify-center items-center min-h-[200px]">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
              <p className="ml-3 text-gray-500">Loading search form...</p>
            </div>
          ) : (
            <SearchSection
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              tripType={tripType}
              setTripType={setTripType}
              source={source}
              setSource={setSource}
              destination={destination}
              setDestination={setDestination}
              departureDate={departureDate}
              setDepartureDate={setDepartureDate}
              returnDate={returnDate}
              setReturnDate={setReturnDate}
              pickupTime={pickupTime}
              setPickupTime={setPickupTime}
              dropTime={dropTime}
              setDropTime={setDropTime}
              sourceSearch={sourceSearch}
              setSourceSearch={setSourceSearch}
              destSearch={destSearch}
              setDestSearch={setDestSearch}
              sourceAddress={sourceAddress}
              destinationAddress={destinationAddress}
              onSourcePlaceSelected={handleSourcePlaceSelected}
              onDestinationPlaceSelected={handleDestinationPlaceSelected}
              onSwapLocations={handleSwapLocations}
              handleSearch={handleSearch}
              handleClearFilter={handleClearFilter}
              isSearchEnabled={isSearchEnabled}
              showClearButton={showClearButton}
              animationEnabled={false}
              onDistanceComputed={handleDistanceComputed}
            />
          )}

          {isSearching && (
            <div className="p-6 border-t">
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
              </div>
              <p className="text-center mt-4 text-gray-500">
                Searching for available cabs...
              </p>
            </div>
          )}

          {!isSearching && searchResults.length > 0 && filteredResults.length > 0 && (
            <div className="p-6 border-t">
              <h2 className="text-lg font-semibold mb-4">
                Available Cabs ({filteredResults.length})
              </h2>
              <div className="space-y-4">
                {filteredResults.map((car) => (
                  <CarDetailsCard
                    key={car.id}
                    price={car}
                    distanceKm={distanceKm}
                    pickupTime={pickupTime}
                    sourceState={source?.state}
                    onSelect={(id) => {
                      const returnLegRequired = isReturnLegRequired(tripType);
                      const searchParams = {
                        vertical: activeTab,
                        sourceCity: source?.name,
                        destinationCity: destination?.name,
                        // Exact Places address, when captured - falls back to
                        // the resolved city name for the airport-implicit leg
                        // (that side is never typed by the user).
                        sourceAddress: sourceAddress?.address ?? source?.name,
                        destinationAddress:
                          destinationAddress?.address ?? destination?.name,
                        sourceState: source?.state,
                        departureDate,
                        pickupTime,
                        returnDate: returnLegRequired ? returnDate : undefined,
                        dropTime: returnLegRequired ? dropTime : undefined,
                        tripType,
                        distanceKm,
                      };
                      history.push(`/cab-details/${id}`, { searchParams });
                    }}
                    isAvailable={true}
                  />
                ))}
              </div>
            </div>
          )}

          {!isSearching && searchResults.length === 0 && searchAttempted && (
            <div className="p-6 border-t text-center flex items-center justify-center min-h-[300px]">
              <div className="max-w-md mx-auto">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    vectorEffect="non-scaling-stroke"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2z"
                  />
                </svg>
                <h3 className="mt-2 text-lg font-medium text-gray-900">
                  No Cabs Found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  We couldn't find any available cabs for the selected route and
                  time. Please try adjusting your search criteria.
                </p>
              </div>
            </div>
          )}

          {!isSearching && searchResults.length > 0 && filteredResults.length === 0 && (
            <div className="p-6 border-t text-center flex items-center justify-center min-h-[300px]">
              <div className="max-w-md mx-auto">
                <h3 className="mt-2 text-lg font-medium text-gray-900">
                  No Cabs Match Your Filters
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchResults.length} cab{searchResults.length === 1 ? "" : "s"}{" "}
                  available for this route, but none match the selected filters.
                  Try clearing some filters in the sidebar.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default Dashboard;
